"use client";

import { useTheme } from "./theme-provider";
import { Sun, Moon, Monitor } from "lucide-react";
import { useState, useRef, useEffect, useSyncExternalStore } from "react";
import "./theme-toggle.css";

type Theme = "light" | "dark" | "system";

const THEME_OPTIONS: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
];

interface ThemeToggleProps {
    /** Use "light" for navbars on dark backgrounds (reader pages) */
    variant?: "default" | "light";
}

const subscribeHydration = () => () => {};

export function ThemeToggle({ variant = "default" }: ThemeToggleProps) {
    const { theme, setTheme, resolvedTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const mounted = useSyncExternalStore(subscribeHydration, () => true, () => false);
    const ref = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!open) return;
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    const Icon = mounted && resolvedTheme === "dark" ? Moon : Sun;

    if (!mounted) {
        return (
            <div className={`theme-toggle-wrapper`} ref={ref}>
                <button
                    type="button"
                    className={`theme-toggle-btn ${variant === "light" ? "theme-toggle-btn--light" : ""}`}
                    aria-label="Toggle theme"
                    data-testid="theme-toggle"
                >
                    <span className="theme-toggle-icon" style={{ width: 20, height: 20 }} />
                </button>
            </div>
        );
    }

    return (
        <div className="theme-toggle-wrapper" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`theme-toggle-btn ${variant === "light" ? "theme-toggle-btn--light" : ""}`}
                aria-label="Toggle theme"
                data-testid="theme-toggle"
            >
                <Icon className="theme-toggle-icon" />
            </button>

            {open && (
                <div className="theme-toggle-dropdown" role="listbox" aria-label="Theme options">
                    {THEME_OPTIONS.map(({ value, label, icon: OptionIcon }) => (
                        <button
                            key={value}
                            type="button"
                            role="option"
                            aria-selected={theme === value}
                            className={`theme-toggle-option ${
                                theme === value ? "theme-toggle-option--active" : ""
                            }`}
                            onClick={() => {
                                setTheme(value);
                                setOpen(false);
                            }}
                        >
                            <OptionIcon className="theme-toggle-option-icon" />
                            <span>{label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
